import { EditorErrorBoundary } from "@prismicio/editor-fields"
import {
  Box,
  DefaultErrorMessage,
  dialogErrorImage,
  Skeleton,
} from "@prismicio/editor-ui"
import { Suspense } from "react"
import { z } from "zod"

import {
  parseRouteParams,
  useRedirect,
  useRouteParams,
} from "../../app/routing"
import { preloadPreviewUrls } from "../../app/services/core"
import {
  prefetchReleaseSize,
  preloadReleasePermissions,
  type Release,
} from "../../app/services/release"
import { useRepositoryReleases } from "../../document/documentRouting"
import { DocumentListBody } from "../DocumentListBody"
import { DocumentListBodySkeleton } from "../DocumentListBody/DocumentListBody"
import { DocumentListBodyLayout } from "../DocumentListBody/DocumentListBodyLayout"
import { useDocumentListData } from "../DocumentListBody/useDocumentListData"
import { DocumentListHeader } from "../DocumentListHeader/DocumentListHeader"
import { DocumentListLayout } from "../documentListLayout"
import { DocumentListSearchParamsProvider } from "../DocumentListSearchParamsProvider"
import { DocumentListSidePanel } from "../DocumentListSidePanel"
import { ReleaseDate } from "./ReleaseDate"
import { ReleaseDeleteButton } from "./ReleaseDeleteButton"
import { ReleaseDownloadCSVButton } from "./ReleaseDownloadCsvButton"
import { ReleaseNameInput } from "./ReleaseNameInput"
import { ReleasePageHeader } from "./ReleasePageHeader"
import { ReleasePreview } from "./ReleasePreview"
import { ReleasePublishButton } from "./ReleasePublishButton"
import { ReleaseStatusIndicator } from "./ReleaseStatusIndicator"

export function ReleasePage() {
  const releaseId = useReleaseIdFromRoute()

  return (
    <DocumentListSearchParamsProvider>
      <DocumentListLayout
        header={<DocumentListHeader tab="upcoming" />}
        sidePanel={<DocumentListSidePanel selectedMenuItem="main-space" />}
        body={
          <EditorErrorBoundary renderError={renderReleasePageError}>
            <Suspense
              fallback={<ReleasePageBodySkeleton releaseId={releaseId} />}
            >
              <ReleasePageBody />
            </Suspense>
          </EditorErrorBoundary>
        }
      />
    </DocumentListSearchParamsProvider>
  )
}

export function prefetchReleasePage(routeParams: unknown) {
  const { releaseId } = parseRouteParams(routeParams, releaseParams)

  preloadReleasePermissions(releaseId)
  prefetchReleaseSize(releaseId)
  preloadPreviewUrls()

  return null
}

function ReleasePageBody() {
  const release = useReleaseFromRoute()

  const routeReleaseId = useReleaseIdFromRoute()
  const redirect = !release

  useRedirect(redirect, {
    to: "/upcoming",
    title: "Release not found",
    subtitle: `The release with id ${routeReleaseId} might have been already released or deleted. You have been redirected to the upcoming releases page.`,
  })

  if (redirect) return null

  return (
    <DocumentListBodyLayout>
      <ReleasePageHeader
        title={
          <>
            <ReleaseStatusIndicator release={release} />
            <ReleaseNameInput
              // key used to ensure text-area updates when release id changes
              key={release.id}
              defaultValue={release.label}
              releaseId={release.id}
            />
          </>
        }
        actions={<ReleasePageHeaderActions release={release} />}
      />
      <DocumentListBody tab="upcoming" releaseId={release.id} />
    </DocumentListBodyLayout>
  )
}

function ReleasePageHeaderSkeleton() {
  return (
    <ReleasePageHeader
      title={<Skeleton width={200} height={30} />}
      actions={
        <Box gap={8}>
          <Skeleton key="release-date" width={159} height={40} />
          <Skeleton key="preview" width={135} height={40} />
          <Skeleton key="delete" width={102} height={40} />
          <Skeleton key="publish" width={108} height={40} />
        </Box>
      }
    />
  )
}

interface ReleasePageHeaderActionsProps {
  release: Release
}

function ReleasePageHeaderActions(props: ReleasePageHeaderActionsProps) {
  const { release } = props

  const {
    data: { documents },
  } = useDocumentListData({
    tab: "upcoming",
    releaseId: release.id,
  })

  return (
    <Box gap={8}>
      <ReleaseDate release={release} size="large" />
      <ReleasePreview releaseId={release.id} releaseDocument={documents[0]} />
      <ReleaseDownloadCSVButton release={release} />
      <ReleaseDeleteButton release={release} size="large" />
      <ReleasePublishButton release={release} size="large" />
    </Box>
  )
}

interface ReleasePageBodySkeletonProps {
  releaseId: string
}

function ReleasePageBodySkeleton(props: ReleasePageBodySkeletonProps) {
  const { releaseId } = props
  return (
    <DocumentListBodyLayout>
      <ReleasePageHeaderSkeleton />
      <DocumentListBodySkeleton tab="upcoming" releaseId={releaseId} />
    </DocumentListBodyLayout>
  )
}

const releaseParams = { releaseId: z.string() }

function useReleaseIdFromRoute() {
  const params = useRouteParams({ path: releaseParams })
  return params.path.releaseId
}

function useReleaseFromRoute() {
  const releases = useRepositoryReleases()
  const releaseId = useReleaseIdFromRoute()
  return releases.find((release) => release.id === releaseId)
}

function renderReleasePageError() {
  return (
    <Box
      height="100%"
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching release."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}
